import { IAngularEvent, IRootScopeService, IWindowService } from "angular";
import { IModalService, IModalScope } from "angular-ui-bootstrap";
import { IGoal } from "../models";
import { IPlanningSession, ICloneOrMoveGoalWorkerData, IPlanningSessionConflict } from "@gtmhub/sessions";
import { EnvironmentService } from "@gtmhub/env";
import { AccountResolverService } from "../../state/accountResolverService";
import { GoalService } from "../goalService";
import { localize } from "@gtmhub/localization";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { getToken } from "@gtmhub/auth";
import { ICollection } from "@gtmhub/models";
import { INgRedux } from "@gtmhub/state-management";
import { WebWorkerService } from "../../shared/services/webWorkerService";
import { GoalModalsService } from "../goalModalsService";
import { ICfMap } from "@gtmhub/customFields/models/models";
import { filterActiveAndFutureSessions, getSessionAndOneLevelChildren } from "@gtmhub/sessions/redux/session-selectors";
import { ISessionsStoreState } from "@gtmhub/sessions";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { getCurrentUserId } from "@gtmhub/users";
import { AssigneeService } from "@gtmhub/assignees/services";
import { GoalsChangedEventArgs, goalSelected, OkrEvents } from "@gtmhub/okrs";
import { IStatusData, StatusBarService } from "@gtmhub/shared/components";
import { disableWebpackCdnPublicPath } from "@gtmhub/util/webpack";

export interface IConfirmCloneOrMoveModal {
  cfCounter: number;
  method: MethodType;
  invokerType: string; // "OKR" or "KR", but is not limited
}
export interface IGoalCloneSelected {
  session: IPlanningSession;
  goal: IGoal;
  progress: boolean;
  cloneTasks: boolean;
  cloneChildren: boolean;
  moveChildren: boolean;
  notifications: boolean;
  duplicateInsight: boolean;
}

interface IGenericSessionSelectorScope extends IModalScope {
  selectSessionList(session: IPlanningSession): void;
  selected: { screen: string };
  activeSessions: IPlanningSession[];
}

export type MethodType = "clone" | "move";

export interface ICloneSessionsRootScope extends IRootScopeService {
  renderDir: boolean;
}

export interface ICloneOrMoveGoalScope extends IModalScope {
  indicators: {
    cloning: boolean;
    moving: boolean;
    gettingTasks: boolean;
  };
  goal: IGoal;
  selected: IGoalCloneSelected;
  showSessionSelector: boolean;
  hasOnlyManualMetrics: boolean;
  activeSessions: IPlanningSession[];
  prepareToCloneOrMove(method: MethodType);
  openGoalSelectorModal(method: MethodType): void;
  openSessionSelectorModal(): void;
  cloneOrMove(method: MethodType, selected?: IGoalCloneSelected): void;
  chooseBetweenSessions: boolean;
}

const goalFields =
  "parentId,parentType,ownerIds,assignee{name,email,color,avatar,picture,type},description,links{spec}," +
  "name,dateFrom,dateTo,childrenCount,fullSubTreeCount,metricsCount,designScore,attainment,attainmentTypeString,childrenAggregatedAttainment,fullAggregatedAttainment,sessionId," +
  "tags{id,name,title,isActive,itemsTaggedCount},customFields,obfuscated,private,access,parentGoalSessionId," +
  "workflow{status,reviewersNeeded,reviewers,approvedAt},locked," +
  "tasks{name,externalId,externalSystem,description,status,priority,parentId,ownerId,createdById,dueDate}," +
  "metrics{sessionId,trend,confidence,customFields,description,ownerIds,name,attainment,insightName,fieldName,settings,lastCheckInDate,targetOperator," +
  "format,target,critical,initialValue,actual,goalId,manualType,softDueDate,dueDate,reminderSchedule,progressStatus,links{spec},cascadeType,metricHistory,orderId},orderId";

export class CloneOrMoveGoalCtrl {
  public static $inject = [
    "$rootScope",
    "$scope",
    "$uibModal",
    "$window",
    "EnvironmentService",
    "AccountResolverService",
    "goal",
    "GoalService",
    "UIErrorHandlingService",
    "StatusBarService",
    "WebWorkerService",
    "GoalModalsService",
    "$ngRedux",
    "AssigneeService",
  ];

  constructor(
    private $rootScope: ICloneSessionsRootScope & ITraceRootScopeService,
    private $scope: ICloneOrMoveGoalScope,
    private $uibModal: IModalService,
    private $window: IWindowService,
    private env: EnvironmentService,
    private accountResolverService: AccountResolverService,
    private goal: IGoal,
    private goalService: GoalService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private statusBarService: StatusBarService,
    private webWorkerService: WebWorkerService,
    private goalModalsService: GoalModalsService,
    private $ngRedux: INgRedux,
    private assigneeService: AssigneeService
  ) {
    this.okrEvents = new OkrEvents($rootScope);
    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));
    this.init();
  }
  private webWorker: Worker = null;
  private parentChildMap: { [id: string]: IGoal[] } = {};
  private okrEvents: OkrEvents;
  private init = () => {
    this.$rootScope.traceAction("clone_or_move_goal_modal_init", () => {
      const mapStateToScope = (state: ISessionsStoreState) => {
        return {
          activeSessions: filterActiveAndFutureSessions(state.sessions.items),
        };
      };
      const unsubscribe = this.$ngRedux.connect(mapStateToScope)(this.$scope);
      this.$scope.$on("$destroy", unsubscribe);

      this.$scope.selected = {
        session: {},
        goal: {},
        progress: true,
        cloneTasks: false,
        cloneChildren: false,
        moveChildren: false,
        notifications: false,
        duplicateInsight: false,
      };

      // we need to check if there are any dynamic metrics in this goal
      if (this.goal.metrics && this.goal.metrics.length) {
        this.$scope.hasOnlyManualMetrics = this.goal.metrics.every((metric) => !metric.insightName);
      } else {
        this.$scope.hasOnlyManualMetrics = true;
      }

      this.$scope.prepareToCloneOrMove = this.prepareToCloneOrMove;
      this.$scope.openGoalSelectorModal = this.openGoalSelectorModal;
      this.$scope.openSessionSelectorModal = this.openSessionSelectorModal;
      this.$scope.cloneOrMove = this.cloneOrMove;

      this.$scope.indicators = {
        cloning: false,
        moving: false,
        gettingTasks: false,
      };

      this.$scope.goal = this.goal;
      const removeRollbackListener = this.$rootScope.$on("rollback", this.rollbackClonedItems);

      this.$scope.$on("$destroy", removeRollbackListener);
    });
  };

  private workerData: ICloneOrMoveGoalWorkerData = null;
  private validSessions: IPlanningSession[];

  private rollbackClonedItems = (): void => {
    this.workerData.rollback = true;
    this.sendWorkerMessage(this.workerData);
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    if (args.reason === goalSelected) {
      this.onGoalSelected(args.detail.goal);
    }
  };

  private onGoalSelected = (goal: IGoal) => {
    this.$scope.selected.goal = goal;
    this.$scope.selected.session = {};
    this.validSessions = getSessionAndOneLevelChildren(this.$scope.activeSessions, goal.sessionId);
    this.$scope.showSessionSelector = true;

    if (this.validSessions.length === 1) {
      this.$scope.selected.session = this.validSessions[0];
      this.$scope.selected.goal.sessionId = this.$scope.selected.session.id;
      this.$scope.chooseBetweenSessions = false;
    } else {
      this.$scope.chooseBetweenSessions = true;
    }
  };

  private openGoalSelectorModal = (method: MethodType): void => {
    this.$rootScope.traceAction("open_goal_selector_modal", () => {
      const canSelectSameGoalAsParent = this.shouldAllowToSelectSameGoalAsParent(method);
      this.$scope.selected.goal = null;
      this.$scope.selected.session = null;

      const sessionSelected = (event: IAngularEvent, session: IPlanningSession) => {
        this.$scope.showSessionSelector = false;
        this.$scope.selected.session = session;
      };

      this.$scope.$on("sessionSelected", sessionSelected);

      const unselectableId = canSelectSameGoalAsParent ? null : this.$scope.goal.id;
      this.goalModalsService.openGoalSelectModal({ unselectableId, invokedBy: "CloneOKR" });
    });
  };

  private shouldAllowToSelectSameGoalAsParent = (method: MethodType): boolean => method === "clone";

  private openSessionSelectorModal = (): void => {
    this.$rootScope.traceAction("open_session_selector_modal", () => {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const ctrl = this;
      this.$uibModal.open({
        templateUrl: require("../views/generic-goal-selector.jade"),
        windowClass: "medium",
        controller: [
          "$scope",
          function ($scope: IGenericSessionSelectorScope) {
            $scope.activeSessions = ctrl.validSessions;

            $scope.selected = {
              screen: "sessions",
            };

            $scope.selectSessionList = (session: IPlanningSession): void => {
              ctrl.$scope.selected.session = session;
              ctrl.$scope.selected.goal.sessionId = session.id;
              $scope.$dismiss();
            };
          },
        ],
      });
    });
  };

  private onWebWorkerError = (evt: ErrorEvent): void => {
    this.statusBarService.hide();

    this.uiErrorHandlingService.handleModal({
      body: evt.message,
    });
  };

  private onWebWorkerResponse = (message: { data: ICloneOrMoveGoalWorkerData }): void => {
    switch (message.data.status) {
      case "conflicts":
        {
          this.statusBarService.hide();
          this.openConflictsModal(message.data.conflicts, message.data.session);
        }
        break;
      case "goal_moved":
        this.okrEvents.broadcastGoalsChanged("goalMoved", { goal: message.data.goal });
        break;
      case "rollback":
        this.okrEvents.broadcastGoalsChanged("goalDeleted", { goalId: message.data.goalId });
        break;
      case "starting":
      case "in_progress":
      case "done":
      case "error":
        {
          if (message.data.status === "in_progress" && message.data.action === "clone" && message.data.goal?.id) {
            this.okrEvents.broadcastGoalsChanged("goalCloned", { goal: message.data.goal });
          }

          const data: IStatusData = {
            status: message.data.status,
            action: message.data.action,
          };

          if (message.data.status === "done") {
            data.params = {
              goal: message.data?.goal,
              includeItems: this.$scope.selected.cloneTasks || this.$scope.selected.cloneChildren || this.$scope.selected.moveChildren,
              ...(this.$scope.selected.goal && { targetGoal: this.$scope.selected.goal }),
              ...((message.data.action === "move" || this.$scope.selected.session.id !== this.$scope.goal.sessionId) && { targetSession: this.$scope.selected.session }),
              totalItemsCounter: message.data?.totalItemsCounter,
            };
          }

          this.statusBarService.updateStatus(data);
        }
        break;
    }
  };

  private openConflictsModal = (conflicts: ICollection<IPlanningSessionConflict>, session: IPlanningSession): void => {
    this.$uibModal.open({
      templateUrl: require("../../sessions/views/conflicts.jade"),
      windowClass: "medium",
      controller: "PlanningSessionConflictsCtrl",
      resolve: {
        conflicts: () => conflicts,
        session: () => session,
        onSave: () => () => {
          this.statusBarService.show();
          this.sendWorkerMessage({ ...this.workerData, status: "continue" });
        },
        ignoreAndClone: () => true,
      },
    });
  };

  private spawnWorker = (method: string): void => {
    if (this.$window.Worker) {
      disableWebpackCdnPublicPath(() => {
        this.webWorker = new Worker(new URL(/* webpackChunkName: "clone-or-move.worker" */ "../../web-workers/goals/clone-or-move.worker", import.meta.url));
      });
      this.webWorker.addEventListener("message", this.onWebWorkerResponse);
      this.webWorker.addEventListener("error", this.onWebWorkerError);

      this.workerData = {
        headers: this.webWorkerService.createHttpHeaders({
          event: "objective_" + method + "d",
          goal_id: this.goal.id,
          clone_or_move_all_children: this.$scope.selected.cloneChildren || this.$scope.selected.moveChildren ? "yes" : "no",
          clone_type: this.$scope.selected.progress ? "keep_progress" : "reset_progress",
          clone_tasks: this.$scope.selected.cloneTasks ? "yes" : "no",
          duplicate_insight: this.$scope.selected.duplicateInsight ? "yes" : "no",
          notify: this.$scope.selected.notifications ? "yes" : "no",
          cloned: "true",
        }),
        paths: {
          goals: this.env.getApiEndpoint("/goals"),
          goalsV2: this.env.getApiEndpointV2("/goals"),
          metrics: this.env.getApiEndpoint("/metrics"),
          metricsV2: this.env.getApiEndpointV2("/metrics"),
          tasks: this.env.getApiEndpoint("/tasks"),
          tasksV2: this.env.getApiEndpointV2("/tasks"),
          session: this.env.getApiEndpoint("/sessions"),
          links: this.env.getApiEndpoint("/links"),
          insights: this.env.getApiEndpoint("/insights"),
        },
        info: {
          token: getToken(), // passing additional data to worker
          accountId: this.accountResolverService.getAccountId(), // passing additional data to worker
          currentUserId: getCurrentUserId(),
          selected: this.$scope.selected,
          goal: this.goal,
          parentChildMap: this.parentChildMap,
          method,
        },
      };

      this.sendWorkerMessage(this.workerData);
    } else {
      this.uiErrorHandlingService.handleModal({
        body: localize("not_supported_in_browser"),
      });
    }
  };

  private sendWorkerMessage = (data: unknown): void => {
    this.webWorker.postMessage(data);
  };

  private prepareToCloneOrMove = (method: MethodType): void => {
    const m = this.goal.customFields as ICfMap;
    const cfCounter = m ? Object.keys(m).length : 0;

    if (method === "clone") {
      // check if we have deactivated owners and remove them
      this.goal.ownerIds = this.assigneeService.getActiveAssigneesOnly(this.goal.ownerIds);
    }

    if (cfCounter != 0) {
      this.confirmCloneOrMoveGoalWithCustomFields(method, cfCounter);
    } else {
      this.cloneOrMove(method);
    }
  };

  private cloneOrMove = (method: MethodType, selected?: IGoalCloneSelected): void => {
    //  selected will be passed only on clone from the clone okrs modal
    if (selected) {
      this.$scope.selected = selected;
    }

    this.$rootScope.traceAction(`prepare_to_${method}`, () => {
      if (!this.$scope.selected.cloneChildren && !this.$scope.selected.moveChildren) {
        this.goal.childrenCount = 0;
        this.startCloneOrMove(method);
      } else {
        this.goalService
          .getGoalsWithParams({
            sessionId: this.goal.sessionId,
            metrics: true,
            fields: goalFields,
          })
          .then(
            (goals) => {
              // Update the metrics list with the updates from the above call
              const currentGoal = goals.find((goal) => goal.id === this.goal.id);
              if (!currentGoal) return;

              this.goal.metrics = currentGoal.metrics;

              goals.forEach((goal) => {
                this.parentChildMap[goal.id] = [];

                if (goal.metrics) {
                  goal.metrics.forEach((metric) => {
                    this.parentChildMap[metric.id] = [];
                  });
                }
              });

              goals.forEach((goal) => {
                if (goal.parentId && this.parentChildMap[goal.parentId]) {
                  this.parentChildMap[goal.parentId].push(goal);
                }
              });

              this.startCloneOrMove(method);
            },
            (error) => this.uiErrorHandlingService.handleModal(error)
          );
      }
    });
  };

  private startCloneOrMove = (method: MethodType): void => {
    this.spawnWorker(method);
    this.statusBarService.show();
    // closing for clone happens in another modal
    if (method === "move") {
      this.$scope.$close();
    }
  };

  private confirmCloneOrMoveGoalWithCustomFields = (method: MethodType, cfCounter: number): void => {
    const invokerType = "OKR";

    this.$uibModal
      .open({
        templateUrl: require("../views/modules/confirm-clone-or-move.jade"),
        windowClass: "medium-small",
        controller: [
          "$scope",
          function ($scope: IConfirmCloneOrMoveModal) {
            $scope.method = method;
            $scope.cfCounter = cfCounter;
            $scope.invokerType = invokerType;
          },
        ],
      })
      .result.then(() => this.cloneOrMove(method));
  };
}
